import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  CompanyDetails,
  BasicResponse,
  CreateCompanyRequest,
  CopmanyUser,
  CreateCompanyResponse,
  AddCompanyUserRequest,
  JobPostResponse,
  CreateJobPostRequest,
  EditJobPostRequest,
  CompanyJobPostsResponse,
  Attachments, // Added Attachments
} from "../types/api.d";
import companyService from "../services/company.service";
import jobService from "../services/job.service"; // Import jobService
import { getLocalStorageItem } from "../utils/authUtils";
import { LocalStorageUser } from "../types/localStorage.d";
import { useAuth } from "./AuthContext";
import { CompanyProfileData } from "../types/components";

interface ErrorState {
  companyDetails: string | null;
  jobPosts: string | null;
  jobPost: string | null;
  createJob: string | null;
  editJob: string | null;
  deleteJob: string | null;
  linkCompany: string | null;
  updateCompanyDetails: string | null;
}

interface LoadingState {
  companyDetails: boolean;
  updateCompanyDetails: boolean;
  jobPosts: boolean;
  jobPost: boolean;
  createJob: boolean;
  editJob: boolean;
  deleteJob: boolean;
}

interface CompanyContextType {
  companyDetails: CompanyDetails | null;
  jobPosts: JobPostResponse[] | null;
  jobPost: JobPostResponse | null;
  jobPostCount: number | null; // New: Count of job posts
  loadingState: LoadingState;
  errorState: ErrorState; // Changed from error: string | null
  fetchCompanyDetails: (companyId: string) => Promise<void>;
  updateCompanyDetails: (data: CompanyProfileData) => Promise<void>;
  updateCompanyAttachments: (newAttachment: Attachments) => void; // New function
  updateJobPostAttachments: (newAttachment: Attachments) => void; // New function
  linkCompany: (companyId: string, userId: string) => Promise<void>;
  createJobPost: (
    jobData: CreateJobPostRequest,
    fullJobDetails: EditJobPostRequest
  ) => Promise<number>;
  editJobPost: (jobData: EditJobPostRequest) => Promise<void>;
  saveJobDraft: (
    jobData: CreateJobPostRequest | EditJobPostRequest
  ) => Promise<void>;
  fetchCompanyJobPosts: (companyId: string) => Promise<void>;
  fetchJobPostById: (postId: number) => Promise<JobPostResponse | undefined>;
  deleteJobPost: (postId: number) => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const CompanyProvider = ({ children }: { children: ReactNode }) => {
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails | null>(
    null
  );
  const [jobPosts, setJobPosts] = useState<JobPostResponse[] | null>(null);
  const [jobPost, setJobPost] = useState<JobPostResponse | null>(null);
  const [jobPostCount, setJobPostCount] = useState<number | null>(null); // New state for job post count
  const { user, updateUser } = useAuth();
  const [loadingState, setLoadingState] = useState<LoadingState>({
    companyDetails: true,
    updateCompanyDetails: false,
    jobPosts: false,
    jobPost: false,
    createJob: false,
    editJob: false,
    deleteJob: false,
  });
  const [initialRender, setInitialRender] = useState<boolean>(true);
  const [errorState, setErrorState] = useState<ErrorState>({
    // Changed from error: string | null
    companyDetails: null,
    jobPosts: null,
    jobPost: null,
    createJob: null,
    editJob: null,
    deleteJob: null,
    linkCompany: null,
    updateCompanyDetails: null,
  });

  const setLoading = (key: keyof LoadingState, value: boolean) => {
    setLoadingState((prev) => ({ ...prev, [key]: value }));
  };

  const setError = (key: keyof ErrorState, message: string | null) => {
    setErrorState((prev) => ({ ...prev, [key]: message }));
  };

  const fetchCompanyJobPosts = async (companyId: string) => {
    if (loadingState.jobPosts) return;

    setLoading("jobPosts", true);
    setError("jobPosts", null); // Clear error at start
    try {
      const response: BasicResponse<CompanyJobPostsResponse> =
        await jobService.getAllCompanyJobPosts(companyId);
      if (
        response.status === "success" &&
        response.data &&
        response.data.posts
      ) {
        setJobPosts(response.data.posts);
        setJobPostCount(response.data.posts.length); // Set job post count
      } else {
        setError(
          "jobPosts",
          response.status || "Failed to fetch company job posts"
        );
        setJobPostCount(0); // Set count to 0 on error
      }
    } catch (error: any) {
      console.error("Error fetching company job posts:", error);
      setError(
        "jobPosts",
        error.message || "An unexpected error occurred while fetching job posts"
      );
      setJobPostCount(0); // Set count to 0 on error
    } finally {
      setLoading("jobPosts", false);
    }
  };

  const fetchJobPostById = async (
    postId: number
  ): Promise<JobPostResponse | undefined> => {
    console.log("FetchingJobPosts called", { postId, jobPost, loadingState });
    if (loadingState.jobPost) return;
    setLoading("jobPost", true);
    setError("jobPost", null); // Clear error at start
    try {
      const response: BasicResponse<JobPostResponse> =
        await jobService.getJobPost(postId);
      if (response.status === "success" && response.data) {
        setJobPost(response.data);
        return response.data;
      } else {
        setError("jobPost", response.status || "Failed to fetch job post");
      }
    } catch (error: any) {
      console.error("Error fetching job post:", error);
      setError(
        "jobPost",
        error.message || "An unexpected error occurred while fetching job post"
      );
    } finally {
      setLoading("jobPost", false);
    }
  };

  const createJobPost = async (
    jobData: CreateJobPostRequest,
    fullJobDetails: EditJobPostRequest
  ): Promise<number> => {
    setLoading("createJob", true);
    setError("createJob", null); // Clear error at start
    try {
      // Step 1: Create job post to get postId
      const createResponse: BasicResponse<number> =
        await jobService.createJobPost(jobData);
      if (createResponse.status === "success" && createResponse.data) {
        const newPostId = createResponse.data;
        // Step 2: Edit job post with full details using the new postId
        const editResponse: BasicResponse<boolean> =
          await jobService.editJobPost({
            ...fullJobDetails,
            postId: newPostId,
          });
        if (editResponse.status === "success") {
          if (user && user.role === "Company" && user.companyId) {
            fetchCompanyJobPosts(user.companyId);
          }
        } else {
          setError(
            "createJob",
            editResponse.status ||
              "Failed to set job post details after creation"
          );
        }
        return newPostId;
      } else {
        setError(
          "createJob",
          createResponse.status || "Failed to create job post ID"
        );
        throw new Error("Failed to create job post ID");
      }
    } catch (error: any) {
      console.error("Error creating job post:", error);
      setError(
        "createJob",
        error.message || "An unexpected error occurred while creating job post"
      );
      throw new Error(
        error.message || "An unexpected error occurred while creating job post"
      );
    } finally {
      setLoading("createJob", false);
    }
  };

  const editJobPost = async (jobData: EditJobPostRequest) => {
    setLoading("editJob", true);
    setError("editJob", null); // Clear error at start
    try {
      const response: BasicResponse<boolean> = await jobService.editJobPost(
        jobData
      );
      if (response.status === "success") {
        // Optionally refetch job posts details or update state
        if (user && user.role === "Company" && user.companyId) {
          const updatedJobPost = await fetchJobPostById(jobData.postId);
          setJobPosts((prev) =>
            prev
              ? prev.map((post) =>
                  post.postId == jobData.postId ? updatedJobPost || post : post
                )
              : null
          );
        }
      } else {
        setError("editJob", response.status || "Failed to edit job post");
      }
    } catch (error: any) {
      console.error("Error editing job post:", error);
      setError(
        "editJob",
        error.message || "An unexpected error occurred while editing job post"
      );
    } finally {
      setLoading("editJob", false);
    }
  };

  const saveJobDraft = async (
    jobData: CreateJobPostRequest | EditJobPostRequest
  ) => {
    // This function will determine if it's a new draft or an update to an existing one
    // and call createJobPost or editJobPost accordingly with isDraft set to true.
    setLoading("createJob", true); // Assuming draft saving also uses createJob loading
    setError("createJob", null); // Clear error at start
    try {
      if ((jobData as EditJobPostRequest).postId) {
        await editJobPost({
          ...(jobData as EditJobPostRequest),
          isDraft: true,
        });
      } else {
        // This part needs to be updated to correctly extract companyId and userId
        // from the jobData or user context. For now, using placeholders.
        // This will be refined once the jobData structure for new drafts is clearer.
        // Assuming jobData will contain companyId and createdBy for new drafts
        const initialCreateRequest: CreateJobPostRequest = {
          companyId: (jobData as CreateJobPostRequest).companyId, // Placeholder
          createdBy: (jobData as CreateJobPostRequest).createdBy, // Placeholder
        };
        await createJobPost(initialCreateRequest, {
          ...(jobData as EditJobPostRequest),
          isDraft: true,
        });
      }
    } catch (error: any) {
      console.error("Error saving job draft:", error);
      setError(
        "createJob",
        error.message || "An unexpected error occurred while saving job draft"
      );
    } finally {
      setLoading("createJob", false);
    }
  };

  const deleteJobPost = async (postId: number) => {
    setLoading("deleteJob", true);
    setError("deleteJob", null); // Clear error at start
    try {
      const response: BasicResponse<boolean> = await jobService.deleteJobPost(
        postId
      );
      if (response.status === "success") {
        // Remove the deleted job from the state
        setJobPosts((prev) =>
          prev ? prev.filter((job) => job.postId !== postId) : null
        );
      } else {
        setError("deleteJob", response.status || "Failed to delete job post");
      }
    } catch (error: any) {
      console.error("Error deleting job post:", error);
      setError(
        "deleteJob",
        error.message || "An unexpected error occurred while deleting job post"
      );
    } finally {
      setLoading("deleteJob", false);
    }
  };

  const fetchCompanyDetails = async (companyId: string) => {
    if (loadingState.companyDetails && !initialRender) {
      setInitialRender(false);
      return;
    }
    setLoading("companyDetails", true);
    setError("companyDetails", null); // Clear error at start
    try {
      // throw new Error("Failed to Fetch Company Details TODO remove")
      const response: BasicResponse<CompanyDetails> =
        await companyService.getCompanyDetails(companyId);
      if (response.status === "success" && response.data) {
        setCompanyDetails(response.data);
        setError("companyDetails", null);
        // Fetch job posts after company details are loaded
        await fetchCompanyJobPosts(companyId);
      } else if (companyDetails == null) {
        setError(
          "companyDetails",
          response.status || "Failed to fetch company details"
        );
      }
    } catch (error: any) {
      console.log(error);
      if (error === "Request failed with status code 404") {
        console.log("Company record not found");
        setLoading("companyDetails", false);
      } else if (companyDetails == null) {
        // only set error if no companyDetails are present
        console.log("Exception in fetching companyDetails ,", companyDetails);
        setError(
          "companyDetails",
          error.message || "An unexpected error occurred"
        );
      }
    } finally {
      setLoading("companyDetails", false);
    }
  };

  const linkCompany = async (companyId: string, userId: string) => {
    setError("linkCompany", null); // Clear error at start
    try {
      const designation = "N/A"; // Default designation
      const response = await companyService.addCompanyUser(
        companyId,
        userId,
        designation
      );
      if (response.status === "success" && response.data) {
        updateUser({ companyId: companyId });
        console.log(`User ${userId} linked to company ${companyId}`);
      } else {
        setError(
          "linkCompany",
          response.status || "Failed to link company to user"
        );
        throw new Error("Failed to link company to user");
      }
    } catch (error: any) {
      console.error("Error linking company:", error);
      setError(
        "linkCompany",
        error.message || "An unexpected error occurred during company linking"
      );
      throw error;
    } finally {
      //TODO add a link company loader
      // setLoading("link", false); // Assuming this is part of company setup
    }
  };

  const updateCompanyDetails = async (data: CompanyProfileData) => {
    setError("updateCompanyDetails", null); // Clear error at start
    setLoading("updateCompanyDetails", true); // Set loading for company details update
    try {
      if (!user || user.role !== "Company") {
        throw new Error("User is not a Company.");
      }

      const companyUser = user as CopmanyUser;
      const isNewCompany = !companyUser.companyId;

      const additionalData: { [key: string]: any } = {};
      if (data.panNumber) additionalData.panNumber = data.panNumber;
      if (data.msmeNumber) additionalData.msmeNumber = data.msmeNumber;
      if (data.gstNumber) additionalData.gstNumber = data.gstNumber;
      if (data.registrationType) additionalData.registrationType = data.registrationType;
      if (data.registrationNumber)
        additionalData.registrationNumber = data.registrationNumber;
      if (data.businessType) additionalData.businessType = data.businessType;
      if (data.foundedYear) additionalData.foundedYear = data.foundedYear;
      if (data.city) additionalData.city = data.city;
      if (data.state) additionalData.state = data.state;
      if (data.pincode) additionalData.pincode = data.pincode;
      if (data.country) additionalData.country = data.country;
      if (data.contactPerson) additionalData.contactPerson = data.contactPerson;
      if (data.contactEmail) additionalData.contactEmail = data.contactEmail;
      if (data.contactPhone) additionalData.contactPhone = data.contactPhone;
      if (data.hrEmail) additionalData.hrEmail = data.hrEmail;
      if (data.hrPhone) additionalData.hrPhone = data.hrPhone;
      if (data.linkedinUrl) additionalData.linkedinUrl = data.linkedinUrl;
      if (data.twitterUrl) additionalData.twitterUrl = data.twitterUrl;
      if (data.facebookUrl) additionalData.facebookUrl = data.facebookUrl;
      if (data.instagramUrl) additionalData.instagramUrl = data.instagramUrl;
      // if (data.logo) additionalData.logo = data.logo;
      if (data.registrationStep !== undefined)
        additionalData.registrationStep = data.registrationStep;

      const createCompanyRequest: CreateCompanyRequest & {
        [key: string]: any;
      } = {
        companyId: companyUser.companyId,
        companyName: data.companyName,
        webpageUrl: data.website != "" ? data.website : undefined,
        description: data.description != "" ? data.description : undefined,
        empStrength: data.companySize != "" ? data.companySize : undefined,
        address: data.address != "" ? data.address : undefined,
        industry: data.industry != "" ? data.industry : undefined,
        gstin: data.gstNumber != "" ? data.gstNumber : undefined,
        shouldSubmitProfile: data.shouldSubmitProfile,
        ...additionalData,
      };

      const response = await companyService.createCompany(createCompanyRequest);
      if (response.status === "success" && response.data) {
        if (isNewCompany && response.data.companyId) {
          if (
            !user.companyId &&
            response.data.companyId &&
            response.data.companyId != "00000000-0000-0000-0000-000000000000"
          ) {
            // Check if user.companyId is null after successful company creation
            await linkCompany(response.data.companyId, user.userId);
          } else {
            throw new Error(
              response.data.companyId == "00000000-0000-0000-0000-000000000000"
                ? "Company Name Already Exists"
                : "Failed to update company details"
            );
          }
        }
        await fetchCompanyDetails(response.data.companyId);
      } else {
        setError(
          "updateCompanyDetails",
          response.status || "Failed to update company details"
        );
        throw new Error("Failed to update company details");
      }
    } catch (error: any) {
      console.error("Error updating company details:", error);
      throw error;
    } finally {
      setLoading("updateCompanyDetails", false);
      // setLoading("companyDetails", false);
    }
  };

  const updateCompanyAttachments = (newAttachment: Attachments) => {
    setCompanyDetails((prevDetails) => {
      if (!prevDetails) return null;

      const existingAttachments = prevDetails.attachments || [];
      const updatedAttachments = existingAttachments
        .filter((att) => att.typeId !== newAttachment.typeId) // Remove old attachment of the same type
        .concat(newAttachment); // Add the new attachment

      return {
        ...prevDetails,
        attachments: updatedAttachments,
      };
    });
  };
  const updateJobPostAttachments = (
    newAttachment: Attachments
  ) => {
    setJobPost((prevDetails) => {
      if (!prevDetails) return null;

      const existingAttachments = prevDetails.attachments || [];
      const updatedAttachments = existingAttachments
        .filter((att) => att.typeId !== newAttachment.typeId) // Remove old attachment of the same type
        .concat(newAttachment); // Add the new attachment
      const newPost = {
        ...prevDetails,
        attachments: updatedAttachments,
      };
      setJobPosts((prevDetails) => {
        if (!prevDetails) return null;

        // const existingAttachments = prevDetails.attachments || [];
        const updatedAttachments = prevDetails
          .filter((post) => post.postId !== newPost.postId) // remove existing post
          .concat(newPost); // Add the new attachment

        return updatedAttachments;
      });
      return newPost;
    });
  };

  useEffect(() => {
    if (user && user.role === "Company" && user.companyId) {
      fetchCompanyDetails(user.companyId);
    } else {
      setLoading("companyDetails", false);
    }
  }, [user]);

  return (
    <CompanyContext.Provider
      value={{
        companyDetails,
        jobPosts,
        jobPost,
        jobPostCount, // New: Expose jobPostCount
        loadingState,
        errorState,
        fetchCompanyDetails,
        updateCompanyDetails,
        updateCompanyAttachments, // Expose new function
        updateJobPostAttachments, // Expose new function
        linkCompany,
        createJobPost,
        editJobPost,
        saveJobDraft,
        fetchCompanyJobPosts,
        fetchJobPostById,
        deleteJobPost,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error("useCompany must be used within a CompanyProvider");
  }
  return context;
};
